import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { CheckCircle2, TrendingUp, Rocket, ExternalLink, ShieldCheck, Headphones, Store, Target, Wallet, BadgeCheck, IndianRupee, Star, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inr = (n) => '\u20b9' + Number(n || 0).toLocaleString('en-IN');

// ---------- What You Get after ₹9 activation ----------
export const WhatYouGet = ({ onActivate, activationFee = 9 }) => {
  const items = [
    { icon: Store, title: 'Ready-to-sell Store', desc: 'We design & launch your Shopify dropshipping website with a winning product.' },
    { icon: Target, title: 'Meta Ads Setup', desc: 'Professional ad campaigns created & optimised by our media buyers.' },
    { icon: Wallet, title: 'Ad Wallet & Tracking', desc: 'Add ad funds and track every rupee of spend live from your dashboard.' },
    { icon: Headphones, title: 'Dedicated Manager', desc: 'A personal account manager on WhatsApp to guide you daily.' },
    { icon: BadgeCheck, title: 'Winning Products', desc: 'Access to tested, high-margin products picked by our research team.' },
    { icon: TrendingUp, title: 'Profit Wallet', desc: 'Your order profits collect in your wallet — withdraw anytime.' },
  ];
  return (
    <section id="what-you-get" className="py-16 md:py-24 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">What you get for just {inr(activationFee)}</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Everything you need to start earning</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">One small activation fee unlocks your entire done-for-you dropshipping business. No tech skills needed.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <div key={it.title} className="group bg-slate-50 hover:bg-white rounded-2xl p-6 border border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <it.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-slate-900 text-lg">{it.title}</h3>
              <p className="text-slate-500 text-sm mt-1">{it.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Button onClick={onActivate} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold rounded-xl shadow-lg shadow-blue-600/20" data-testid="whatyouget-activate-btn">
            <Rocket className="w-5 h-5 mr-2" /> Activate for {inr(activationFee)}
          </Button>
          <p className="text-xs text-slate-400 mt-3 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> Secure one-time payment via Razorpay</p>
        </div>
      </div>
    </section>
  );
};

// ---------- Earnings by Plan ----------
export const PlanEarnings = ({ onActivate }) => {
  const [plans, setPlans] = useState([]);
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/plans-info`); if (Array.isArray(data?.plans)) setPlans(data.plans); } catch (_e) {}
    })();
  }, []);
  if (!plans.length) return null;
  const accent = {
    Basic: 'from-slate-600 to-slate-800',
    Advanced: 'from-blue-600 to-cyan-500',
    Professional: 'from-amber-500 to-orange-600',
  };
  return (
    <section id="earnings" className="py-16 md:py-24 px-4 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="inline-block bg-green-100 text-green-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">How much can you earn?</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Choose your website plan after activation</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">After your {inr(9)} activation, pick a website plan inside your dashboard. Here's your realistic earning potential.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((p) => {
            const e = p.potentialEarnings || {};
            const popular = p.name === 'Advanced';
            return (
              <div key={p.name} className={`relative bg-white rounded-2xl border ${popular ? 'border-blue-300 shadow-xl scale-[1.02]' : 'border-slate-100 shadow-sm'} overflow-hidden`}>
                {popular && <div className="absolute top-4 right-4 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>}
                <div className={`bg-gradient-to-br ${accent[p.name] || accent.Basic} p-6 text-white`}>
                  <h3 className="text-2xl font-extrabold">{p.name}</h3>
                  <p className="text-white/80 text-sm mt-1">{p.tagline}</p>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-sm text-white/80">Website setup</span>
                    <span className="text-xl font-bold">{p.storeFee > 0 ? inr(p.storeFee) : 'FREE'}</span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="bg-green-50 rounded-xl p-4 mb-4 text-center border border-green-100">
                    <div className="text-xs text-green-700 font-medium">Potential monthly profit</div>
                    <div className="text-2xl font-extrabold text-green-700 mt-1">{inr(e.monthlyLow)} - {inr(e.monthlyHigh)}</div>
                    <div className="text-[11px] text-slate-400 mt-1">~{inr(e.perOrderProfit)}/order &middot; {e.ordersPerDayLow}-{e.ordersPerDayHigh} orders/day</div>
                  </div>
                  <div className="space-y-2">
                    {(p.features || []).map((f) => (
                      <div key={f} className="flex items-start gap-2 text-sm text-slate-600"><CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" /> {f}</div>
                    ))}
                  </div>
                  <div className="mt-4 text-xs text-slate-400 flex items-center gap-1"><Target className="w-3 h-3" /> Recommended ad budget: {inr(p.recommendedAdsBudget)}/day</div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <Button onClick={onActivate} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold rounded-xl" data-testid="earnings-activate-btn">
            <Rocket className="w-5 h-5 mr-2" /> Start with {inr(9)} Activation
          </Button>
          <p className="text-xs text-slate-400 mt-3">*Earnings depend on effort, ad budget & market. These are typical ranges, not guarantees.</p>
        </div>
      </div>
    </section>
  );
};

// ---------- Website Showcase (plan-wise, admin managed) ----------
export const WebsiteShowcase = ({ onActivate }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('All');
  useEffect(() => {
    (async () => {
      try { const { data } = await axios.get(`${API}/website-showcase`); if (Array.isArray(data)) setItems(data); } catch (_e) {}
    })();
  }, []);
  if (!items.length) return null;
  const PLAN_ORDER = ['Basic', 'Advanced', 'Professional'];
  const present = Array.from(new Set(items.map((i) => i.plan).filter((p) => p && p !== 'All')));
  const ordered = PLAN_ORDER.filter((p) => present.includes(p)).concat(present.filter((p) => !PLAN_ORDER.includes(p)));
  const plans = ['All', ...ordered];
  const shown = filter === 'All' ? items : items.filter((i) => i.plan === filter || i.plan === 'All');
  return (
    <section id="showcase" className="py-16 md:py-24 px-4 bg-slate-50">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <span className="inline-block bg-purple-100 text-purple-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">Real stores we&apos;ve built</span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900">Website showcase by plan</h2>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">See the quality of dropshipping store you&apos;ll get for each plan — designed to convert visitors into buyers.</p>
        </div>
        {plans.length > 1 && (
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {plans.map((p) => (
              <button key={p} onClick={() => setFilter(p)} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${filter === p ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'}`}>{p === 'All' ? 'All Plans' : p}</button>
            ))}
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {shown.map((it) => (
            <div key={it.id} className="group bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden hover:shadow-xl transition-all">
              <div className="relative aspect-[4/3] overflow-hidden bg-slate-200">
                {it.imageUrl ? (
                  <img src={it.imageUrl.startsWith('http') ? it.imageUrl : `${BACKEND_URL}${it.imageUrl}`} alt={it.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400"><Store className="w-10 h-10" /></div>
                )}
                {it.plan && it.plan !== 'All' && <span className="absolute top-3 left-3 bg-white/90 text-slate-800 text-xs font-bold px-2.5 py-1 rounded-full">{it.plan}</span>}
              </div>
              <div className="p-5">
                <h3 className="font-bold text-slate-900">{it.title}</h3>
                {it.description && <p className="text-slate-500 text-sm mt-1 line-clamp-2">{it.description}</p>}
                {it.liveUrl && (
                  <a href={it.liveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 text-sm font-medium mt-3 hover:underline">
                    View Live Store <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-10">
          <Button onClick={onActivate} className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-base font-semibold rounded-xl" data-testid="showcase-activate-btn">
            <Rocket className="w-5 h-5 mr-2" /> Get a store like this \u2014 Activate {inr(9)}
          </Button>
        </div>
      </div>
    </section>
  );
};

// ---------- Trust bar ----------
export const TrustBar = () => {
  const items = [
    { icon: ShieldCheck, label: '100% Secure Payments' },
    { icon: Star, label: '4.8/5 Client Rating' },
    { icon: IndianRupee, label: 'Profit Withdrawals' },
    { icon: Headphones, label: 'Daily WhatsApp Support' },
  ];
  return (
    <section className="py-8 px-4 bg-slate-900">
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((it) => (
          <div key={it.label} className="flex flex-col items-center text-center gap-2">
            <it.icon className="w-7 h-7 text-cyan-400" />
            <span className="text-slate-200 text-sm font-medium">{it.label}</span>
          </div>
        ))}
      </div>
    </section>
  );
};
